/**
 * A shed's floor, drawn close enough to teach from: the ground under it, and each of the
 * three things it can stand on — skids on a bed of gravel, pier blocks under built-up
 * beams, or a slab poured in forms — and the frame and the deck over the first two.
 *
 * The same oblique projection as the shed (`shed.ts`, `project`), in feet, at a larger
 * scale: `x` across the front, `y` up, `z` back. Skids and beams run across the front;
 * the rim joists run along them at the front and back, and the joists span between the
 * rims, front to back, on the layout every framer marks — sixteen inches on center, here
 * a twelve by eight foot floor so the deck's courses can stagger.
 *
 * This is a cartoon of a floor, as the films' shed is a cartoon of a shed: the method is
 * the guide's, and nothing in it is a figure from a real plan.
 *
 * Each piece is drawn from how far through its own stage it is, 0 to 1, with no state.
 */

import type { Ink, Paint, Pt } from "./ink.js";
import { noise } from "./ink.js";
import { project, type Projection } from "./shed.js";
import { clamp, ease, mix, span } from "./timing.js";

/** The floor: twelve feet across the front, eight back. */
export const W = 12;
export const D = 8;

/** Heights, in feet: a cartoon's proportions, thick enough to read at the stage's size. */
export const GRAVEL = 0.2;
export const SKID = 0.5;
const SKID_DEEP = 0.45;
export const JOIST = 0.62;
const JOIST_THICK = 0.16;
export const SHEET = 0.09;
/** A pier block's top above the ground, and its footprint at the bottom and the top. */
export const BLOCK = 0.6;
const BLOCK_BASE = 0.95;
const BLOCK_TOP = 0.6;
export const BEAM = 0.62;
const PLY = 0.15;
export const SLAB = 0.42;
const FORM = 0.12;
export const SILL = 0.16;

/** Where the skids or the beams run across the floor, front to back. */
export const LINES: readonly number[] = [0.45, D / 2, D - 0.45];
/** Where the pier blocks stand along each line. */
export const BLOCKS: readonly number[] = [0.7, W / 2, W - 0.7];
/** Sixteen inches on center, in feet. */
export const ON_CENTER = 4 / 3;

/** What the frame stands on: the top of the skids or of the beams. */
export function baseOf(foundation: "skids" | "piers"): number {
  return foundation === "skids" ? GRAVEL + SKID : BLOCK + BEAM;
}

/** The joists' places along the rims, left to right: every sixteen inches, and the last at the end. */
export function joistLines(): number[] {
  const xs: number[] = [];
  for (let x = 0; x < W - JOIST_THICK - 0.05; x += ON_CENTER) {
    xs.push(x);
  }
  xs.push(W - JOIST_THICK);
  return xs;
}

/** A point on the floor's site, in feet, on the stage. */
export function at(proj: Projection, x: number, y: number, z: number): Pt {
  return project(proj, x, y, z);
}

/** A box in feet, drawn as its right side, its top and its front: the faces the projection shows. */
export function box(
  ink: Ink,
  proj: Projection,
  [x0, x1]: readonly [number, number],
  [y0, y1]: readonly [number, number],
  [z0, z1]: readonly [number, number],
  fill: string,
  side: string,
  paint: Paint = {},
): void {
  const P = (x: number, y: number, z: number): Pt => at(proj, x, y, z);
  const base: Paint = { width: 1.8, overshoot: false, offset: 0.6, ...paint };
  ink.shape([P(x1, y0, z0), P(x1, y0, z1), P(x1, y1, z1), P(x1, y1, z0)], { ...base, fill: side });
  ink.shape([P(x0, y1, z0), P(x1, y1, z0), P(x1, y1, z1), P(x0, y1, z1)], { ...base, fill });
  ink.shape([P(x0, y0, z0), P(x1, y0, z0), P(x1, y1, z0), P(x0, y1, z0)], { ...base, fill });
}

/** How far piece `i` of `n` has arrived as its stage goes 0 to 1, a few on their way at once. */
export function arrive(k: number, i: number, n: number, overlap = 2): number {
  const each = 1 / (n + overlap - 1);
  return span(k, i * each, (i + overlap) * each);
}

/** When piece `i` of `n` touches down, as a fraction of its stage, for a drop eased by `ease.bounce`. */
export function touchdown(i: number, n: number, overlap = 2, lands = 0.72): number {
  const each = 1 / (n + overlap - 1);
  return (i + overlap * lands) * each;
}

// ---- the ground ------------------------------------------------------------------------

/**
 * The patch of ground the floor goes on: dirt with lumps in it until it is cleared, and
 * the corner stakes once it is. `rough` is how lumpy, 0 raked flat to 1 as found.
 */
export function ground(ink: Ink, proj: Projection, rough: number): void {
  const p = ink.palette;
  const margin = 1.6;
  const corners = [
    at(proj, -margin, 0, -margin),
    at(proj, W + margin, 0, -margin),
    at(proj, W + margin, 0, D + margin),
    at(proj, -margin, 0, D + margin),
  ];
  ink.shape(corners, { fill: p.shadow, fillAlpha: 0.95, width: 2, offset: 1.5, wobble: 2 });
  // Lumps and stones, where the ground has not been cleared yet.
  if (rough > 0) {
    for (let i = 0; i < 14; i += 1) {
      const x = mix(-0.8, W + 0.8, (noise(i, 11) + 1) / 2);
      const z = mix(-0.6, D + 0.6, (noise(i, 23) + 1) / 2);
      const c = at(proj, x, 0, z);
      const r = (10 + Math.abs(noise(i, 37)) * 16) * proj.scale * 0.025;
      ink.ellipse(c.x, c.y - r * 0.3 * rough, r, r * 0.45 * rough + 0.5, { fill: p.woodDark, fillAlpha: 0.55 * rough, width: 1.6, alpha: rough, offset: 0.5 });
    }
  }
}

// ---- skids -----------------------------------------------------------------------------

/** The bed of gravel under skids: spread from the back toward the front, and tamped. */
export function gravelBed(ink: Ink, proj: Projection, k: number): void {
  if (k <= 0) {
    return;
  }
  const p = ink.palette;
  const reach = mix(D + 0.8, -0.8, ease.inOut(k));
  const x0 = -0.8;
  const x1 = W + 0.8;
  const z1 = D + 0.8;
  const top = GRAVEL;
  ink.shape([at(proj, x1, 0, reach), at(proj, x1, 0, z1), at(proj, x1, top, z1), at(proj, x1, top, reach)], {
    fill: p.roof,
    fillAlpha: 0.45,
    width: 1.6,
    offset: 0.5,
    overshoot: false,
  });
  ink.shape([at(proj, x0, top, reach), at(proj, x1, top, reach), at(proj, x1, top, z1), at(proj, x0, top, z1)], {
    fill: p.roof,
    fillAlpha: 0.25,
    width: 1.8,
    offset: 0.5,
    overshoot: false,
  });
  ink.shape([at(proj, x0, 0, reach), at(proj, x1, 0, reach), at(proj, x1, top, reach), at(proj, x0, top, reach)], {
    fill: p.roof,
    fillAlpha: 0.55,
    width: 1.8,
    offset: 0.5,
    overshoot: false,
  });
  // The stones on top.
  for (let i = 0; i < 90; i += 1) {
    const x = mix(x0 + 0.2, x1 - 0.2, (noise(i, 5) + 1) / 2);
    const z = mix(-0.6, D + 0.6, (noise(i, 9) + 1) / 2);
    if (z < reach) {
      continue;
    }
    const c = at(proj, x, top, z);
    ink.dot(c.x, c.y, 2.2 + Math.abs(noise(i, 13)) * 1.6, p.ink, 0.45);
  }
}

/**
 * The three skids, dropped onto the gravel back to front. `shift` slides the back one
 * along its length, in feet: what squaring them corrects.
 */
export function skids(ink: Ink, proj: Projection, k: number, shift = 0): void {
  const p = ink.palette;
  const order = [2, 1, 0];
  order.forEach((line, i) => {
    const kk = arrive(k, i, 3, 1.6);
    if (kk <= 0) {
      return;
    }
    const lift = (1 - ease.bounce(kk)) * 4;
    const z = LINES[line]!;
    const dx = line === 2 ? shift : 0;
    box(ink, proj, [-0.3 + dx, W + 0.3 + dx], [GRAVEL + lift, GRAVEL + SKID + lift], [z - SKID_DEEP / 2, z + SKID_DEEP / 2], p.grass, p.siding);
  });
}

/** The skids' outside corners on their tops: front left, front right, back left, back right. */
export function skidCorners(proj: Projection, shift: number): [Pt, Pt, Pt, Pt] {
  const top = GRAVEL + SKID;
  const front = LINES[0]! - SKID_DEEP / 2;
  const back = LINES[2]! - SKID_DEEP / 2;
  return [
    at(proj, -0.3, top, front),
    at(proj, W + 0.3, top, front),
    at(proj, -0.3 + shift, top, back),
    at(proj, W + 0.3 + shift, top, back),
  ];
}

// ---- piers -----------------------------------------------------------------------------

/** The string lines and their stakes, around the outside of the floor. */
export function strings(ink: Ink, proj: Projection, k: number): void {
  if (k <= 0) {
    return;
  }
  const p = ink.palette;
  const y = 0.9;
  const c = [
    { x: -0.6, z: -0.6 },
    { x: W + 0.6, z: -0.6 },
    { x: W + 0.6, z: D + 0.6 },
    { x: -0.6, z: D + 0.6 },
  ];
  c.forEach((corner, i) => {
    const kk = span(k, i * 0.12, i * 0.12 + 0.3);
    if (kk <= 0) {
      return;
    }
    const foot = at(proj, corner.x, 0, corner.z);
    const head = at(proj, corner.x, y + 0.2, corner.z);
    const drop = (1 - ease.bounce(kk)) * 30;
    ink.line(foot.x, foot.y - drop, head.x, head.y - drop, { width: 4.5, color: p.woodDark, overshoot: false });
  });
  const pulled = span(k, 0.5, 1);
  [0, 1, 2, 3].forEach((i) => {
    const kk = span(pulled, i * 0.2, i * 0.2 + 0.4);
    if (kk <= 0) {
      return;
    }
    const a = c[i]!;
    const b = c[(i + 1) % 4]!;
    const from = at(proj, a.x, y, a.z);
    const to = at(proj, mix(a.x, b.x, ease.out(kk)), y, mix(a.z, b.z, ease.out(kk)));
    ink.line(from.x, from.y, to.x, to.y, { width: 1.6, color: p.signal, overshoot: false, wobble: 0.3 });
  });
}

/** Where pier block `i` (line-major, back line first) stands, in feet. */
export function blockAt(i: number): { x: number; z: number } {
  const line = 2 - Math.floor(i / BLOCKS.length);
  return { x: BLOCKS[i % BLOCKS.length]!, z: LINES[line]! };
}

/**
 * The pier blocks: each hole dug (`dug`), then each block dropped into it (`set`), back
 * line first. `proud` lifts one block by a little: the one the level finds high.
 */
export function piers(ink: Ink, proj: Projection, dug: number, set: number, proud: { index: number; by: number } = { index: -1, by: 0 }): void {
  const p = ink.palette;
  const n = LINES.length * BLOCKS.length;
  for (let i = 0; i < n; i += 1) {
    const { x, z } = blockAt(i);
    const hole = arrive(dug, i, n, 2);
    if (hole > 0 && arrive(set, i, n, 2) < 1) {
      const c = at(proj, x + BLOCK_BASE / 2, 0, z);
      const r = BLOCK_BASE * 0.75 * proj.scale * ease.out(hole);
      ink.ellipse(c.x, c.y, r, r * 0.42, { fill: p.woodDark, fillAlpha: 0.8, width: 1.8, offset: 0 });
    }
  }
  for (let i = 0; i < n; i += 1) {
    const kk = arrive(set, i, n, 2);
    if (kk <= 0) {
      continue;
    }
    const { x, z } = blockAt(i);
    const lift = (1 - ease.bounce(kk)) * 3 + (i === proud.index ? proud.by : 0);
    pierBlock(ink, proj, x, z, lift);
  }
}

/** One pier block: a tapered block of concrete, its top a little smaller than its base. */
function pierBlock(ink: Ink, proj: Projection, x: number, z: number, lift: number): void {
  const p = ink.palette;
  const b = BLOCK_BASE / 2;
  const t = BLOCK_TOP / 2;
  const cx = x;
  const y0 = lift;
  const y1 = BLOCK + lift;
  const P = (dx: number, y: number, dz: number): Pt => at(proj, cx + dx, y, z + dz);
  const paint: Paint = { width: 1.8, overshoot: false, offset: 0.6 };
  ink.shape([P(b, y0, -b), P(b, y0, b), P(t, y1, t), P(t, y1, -t)], { ...paint, fill: p.roof, fillAlpha: 0.75 });
  ink.shape([P(-b, y0, -b), P(b, y0, -b), P(t, y1, -t), P(-t, y1, -t)], { ...paint, fill: p.roof, fillAlpha: 0.5 });
  ink.shape([P(-t, y1, -t), P(t, y1, -t), P(t, y1, t), P(-t, y1, t)], { ...paint, fill: p.roof, fillAlpha: 0.3 });
}

/** The top of pier block `i`, at its middle: where the level and the beam bear. */
export function blockTop(proj: Projection, i: number, lift = 0): Pt {
  const { x, z } = blockAt(i);
  return at(proj, x, BLOCK + lift, z);
}

/**
 * The beams, set across the piers back to front: three plies nailed face to face, and
 * the joints in each ply staggered so each lands over a block and never two at one.
 */
export function beams(ink: Ink, proj: Projection, k: number, joints = 0): void {
  const p = ink.palette;
  // Where each ply breaks, if it does: over the middle block in the front ply, nowhere in
  // the others — at least one ply runs through at every block.
  const breaks: readonly (number | undefined)[] = [undefined, BLOCKS[1], undefined];
  [2, 1, 0].forEach((line, i) => {
    const kk = arrive(k, i, 3, 1.6);
    if (kk <= 0) {
      return;
    }
    const lift = (1 - ease.bounce(kk)) * 3;
    const z = LINES[line]!;
    const y: [number, number] = [BLOCK + lift, BLOCK + BEAM + lift];
    for (let ply = 2; ply >= 0; ply -= 1) {
      const z0 = z - (PLY * 3) / 2 + ply * PLY;
      box(ink, proj, [-0.3, W + 0.3], y, [z0, z0 + PLY], p.wood, p.woodDark, { width: 1.5 });
    }
    // The joint in the front ply, over the middle block.
    const at0 = breaks[1];
    if (at0 !== undefined && kk >= 1) {
      const a = at(proj, at0, y[0], z - (PLY * 3) / 2);
      const b = at(proj, at0, y[1], z - (PLY * 3) / 2);
      ink.line(a.x, a.y, b.x, b.y, { width: 2.2, overshoot: false });
      if (joints > 0) {
        ink.circle((a.x + b.x) / 2, (a.y + b.y) / 2, 20 * ease.back(clamp(joints * 1.5)), { width: 3.4, color: p.accent, alpha: clamp(joints * 3) });
      }
    }
  });
}

// ---- the slab --------------------------------------------------------------------------

/** How far each part of the slab's work is: the forms, the gravel, the barrier, the mesh, the concrete, the strike-off. */
export interface SlabWork {
  forms: number;
  gravel: number;
  barrier: number;
  mesh: number;
  pour: number;
  strike: number;
  bolts: number;
  sealer: number;
  sills: number;
}

export const NO_SLAB: SlabWork = { forms: 0, gravel: 0, barrier: 0, mesh: 0, pour: 0, strike: 0, bolts: 0, sealer: 0, sills: 0 };

/** Where the anchor bolts go along the sills: near every end, and between. */
export function boltsAt(): { x: number; z: number }[] {
  const inset = 0.25;
  const out: { x: number; z: number }[] = [];
  for (const x of [0.8, W / 2, W - 0.8]) {
    out.push({ x, z: D - inset });
  }
  for (const z of [D - 0.9, D / 2, 0.9]) {
    out.push({ x: inset, z }, { x: W - inset, z });
  }
  for (const x of [0.8, W / 2, W - 0.8]) {
    out.push({ x, z: inset });
  }
  return out;
}

export function slab(ink: Ink, proj: Projection, work: SlabWork): void {
  const p = ink.palette;
  const top = SLAB + FORM;
  const boards: [number, number, number, number][] = [
    [-FORM, W + FORM, D, D + FORM],
    [-FORM, 0, -FORM, D + FORM],
    [W, W + FORM, -FORM, D + FORM],
    [-FORM, W + FORM, -FORM, 0],
  ];
  const form = (i: number): void => {
    const k = arrive(work.forms, i, 4, 1.6);
    if (k <= 0) {
      return;
    }
    const [x0, x1, z0, z1] = boards[i]!;
    const lift = (1 - ease.bounce(k)) * 3;
    box(ink, proj, [x0, x1], [lift, top + lift], [z0, z1], p.wood, p.woodDark);
  };
  form(0);
  form(1);

  const floor = (y: number): Pt[] => [at(proj, 0, y, 0), at(proj, W, y, 0), at(proj, W, y, D), at(proj, 0, y, D)];
  // Gravel, then the barrier over it, then the mesh up on its chairs.
  if (work.gravel > 0 && work.pour < 1) {
    ink.shape(floor(0.08), { fill: p.roof, fillAlpha: 0.3 * work.gravel, width: 1.2, offset: 0.4, overshoot: false });
    for (let i = 0; i < 70; i += 1) {
      const x = mix(0.2, W - 0.2, (noise(i, 41) + 1) / 2);
      const z = mix(0.2, D - 0.2, (noise(i, 43) + 1) / 2);
      if (z > D * work.gravel) {
        continue;
      }
      const c = at(proj, x, 0.08, z);
      ink.dot(c.x, c.y, 2.4, p.ink, 0.45);
    }
  }
  if (work.barrier > 0 && work.pour < 1) {
    const reach = W * ease.inOut(work.barrier);
    ink.shape([at(proj, 0, 0.11, 0), at(proj, reach, 0.11, 0), at(proj, reach, 0.11, D), at(proj, 0, 0.11, D)], {
      fill: p.glass,
      fillAlpha: 0.55,
      width: 1.4,
      offset: 0.4,
      overshoot: false,
    });
  }
  if (work.mesh > 0 && work.pour < 1) {
    const y = 0.2;
    const step = 1;
    for (let x = 0.5; x < W; x += step) {
      const k = span(work.mesh, (x / W) * 0.5, (x / W) * 0.5 + 0.5);
      if (k <= 0) {
        continue;
      }
      const a = at(proj, x, y, 0.3);
      const b = at(proj, x, y, mix(0.3, D - 0.3, ease.out(k)));
      ink.line(a.x, a.y, b.x, b.y, { width: 1.3, alpha: 0.7, overshoot: false, wobble: 0.3 });
    }
    for (let z = 0.5; z < D; z += step) {
      const k = span(work.mesh, 0.3 + (z / D) * 0.4, 0.6 + (z / D) * 0.4);
      if (k <= 0) {
        continue;
      }
      const a = at(proj, 0.3, y, z);
      const b = at(proj, mix(0.3, W - 0.3, ease.out(k)), y, z);
      ink.line(a.x, a.y, b.x, b.y, { width: 1.3, alpha: 0.7, overshoot: false, wobble: 0.3 });
    }
  }

  // The concrete, from the back toward the front: dark while it is wet, lighter struck off.
  const pour = ease.inOut(work.pour);
  if (pour > 0) {
    const front = D * (1 - pour);
    const tone: Paint = { fill: p.roof, width: 1.8, overshoot: false, offset: 0.4 };
    ink.shape([at(proj, W, 0, front), at(proj, W, 0, D), at(proj, W, SLAB, D), at(proj, W, SLAB, front)], { ...tone, fillAlpha: 0.6 });
    ink.shape([at(proj, 0, SLAB, front), at(proj, W, SLAB, front), at(proj, W, SLAB, D), at(proj, 0, SLAB, D)], {
      ...tone,
      fillAlpha: mix(0.55, 0.3, work.strike),
    });
    ink.shape([at(proj, 0, 0, front), at(proj, W, 0, front), at(proj, W, SLAB, front), at(proj, 0, SLAB, front)], { ...tone, fillAlpha: 0.65 });
    if (work.strike > 0 && work.strike < 1) {
      const z = D * work.strike;
      const a = at(proj, -0.2, SLAB + 0.04, z);
      const b = at(proj, W + 0.2, SLAB + 0.04, z);
      ink.line(a.x, a.y, b.x, b.y, { width: 3, color: p.trim, overshoot: false });
    }
  }
  form(2);
  form(3);

  // The anchor bolts, pushed into the wet top, standing proud.
  if (work.bolts > 0) {
    boltsAt().forEach((bolt, i, all) => {
      const k = arrive(work.bolts, i, all.length, 2);
      if (k <= 0) {
        return;
      }
      const sink = (1 - ease.out(k)) * 0.5;
      const foot = at(proj, bolt.x, SLAB, bolt.z);
      const head = at(proj, bolt.x, SLAB + 0.32 + sink, bolt.z);
      ink.line(foot.x, foot.y, head.x, head.y, { width: 3.4, color: p.ink, overshoot: false });
      if (work.sills <= 0) {
        ink.dot(head.x, head.y, 3, p.ink);
      }
    });
  }

  // Sill sealer, and the treated sills dropped over the bolts: tinted, because they are treated.
  const pieces: [number, number, number, number][] = [
    [0, W, D - 0.46, D],
    [0, 0.46, 0, D - 0.46],
    [W - 0.46, W, 0, D - 0.46],
    [0.46, W - 0.46, 0, 0.46],
  ];
  if (work.sealer > 0) {
    pieces.forEach(([x0, x1, z0, z1], i) => {
      const k = arrive(work.sealer, i, 4, 1.5);
      if (k <= 0 || work.sills >= 1) {
        return;
      }
      const reach = mix(x0, x1, ease.out(k));
      const deep = mix(z0, z1, ease.out(k));
      const long = x1 - x0 > z1 - z0;
      const shape = long
        ? [at(proj, x0, SLAB + 0.02, z0), at(proj, reach, SLAB + 0.02, z0), at(proj, reach, SLAB + 0.02, z1), at(proj, x0, SLAB + 0.02, z1)]
        : [at(proj, x0, SLAB + 0.02, z0), at(proj, x1, SLAB + 0.02, z0), at(proj, x1, SLAB + 0.02, deep), at(proj, x0, SLAB + 0.02, deep)];
      ink.shape(shape, { fill: p.accent, fillAlpha: 0.7, width: 1.2, offset: 0.3, overshoot: false });
    });
  }
  if (work.sills > 0) {
    pieces.forEach(([x0, x1, z0, z1], i) => {
      const k = arrive(work.sills, i, 4, 1.5);
      if (k <= 0) {
        return;
      }
      const lift = (1 - ease.bounce(k)) * 1.2;
      box(ink, proj, [x0, x1], [SLAB + 0.03 + lift, SLAB + 0.03 + SILL + lift], [z0, z1], p.grass, p.siding);
    });
    // The nuts drawn down on the bolts that come up through them.
    boltsAt().forEach((bolt) => {
      if (work.sills < 1) {
        return;
      }
      const head = at(proj, bolt.x, SLAB + SILL + 0.06, bolt.z);
      ink.circle(head.x, head.y, 4.5, { fill: p.roof, width: 1.4, offset: 0 });
    });
  }
}

// ---- the frame and the deck ------------------------------------------------------------

/** How far each part of the frame is: the rims set, the layout marked, the joists hung. */
export interface FrameWork {
  rims: number;
  marks: number;
  joists: number;
  /** The deck's sheets, back course first. */
  deck: number;
  /** The deck's end joints, picked out where they land on a joist. */
  joints: number;
}

export const NO_FRAME: FrameWork = { rims: 0, marks: 0, joists: 0, deck: 0, joints: 0 };

/** The deck's sheets, in feet: two courses, the back one starting on a short sheet. */
export const SHEETS: readonly { x0: number; x1: number; z0: number; z1: number }[] = [
  { x0: 0, x1: 4, z0: 4, z1: 8 },
  { x0: 4, x1: 12, z0: 4, z1: 8 },
  { x0: 0, x1: 8, z0: 0, z1: 4 },
  { x0: 8, x1: 12, z0: 0, z1: 4 },
];

/** The floor frame on whatever is under it (`base`, its top in feet): rims, layout, joists, deck. */
export function frame(ink: Ink, proj: Projection, base: number, work: FrameWork): void {
  const p = ink.palette;
  const y0 = base;
  const y1 = base + JOIST;
  const rim = (z0: number, k: number): void => {
    if (k <= 0) {
      return;
    }
    const lift = (1 - ease.bounce(k)) * 3;
    box(ink, proj, [0, W], [y0 + lift, y1 + lift], [z0, z0 + JOIST_THICK], p.wood, p.woodDark);
  };
  rim(D - JOIST_THICK, arrive(work.rims, 0, 2, 1.4));

  const xs = joistLines();
  // The layout, on the back rim's top: an X beside each line, drawn left to right.
  const markBack = (): void => {
    xs.forEach((x, i) => {
      const k = arrive(work.marks, i, xs.length, 2);
      if (k <= 0 || work.joists >= 1) {
        return;
      }
      layoutMark(ink, proj, x, y1, D - JOIST_THICK / 2, k);
    });
  };
  markBack();

  xs.forEach((x, i) => {
    const k = arrive(work.joists, i, xs.length, 2);
    if (k <= 0) {
      return;
    }
    const lift = (1 - ease.bounce(k)) * 1.2;
    box(ink, proj, [x, x + JOIST_THICK], [y0 + lift, y1 + lift], [JOIST_THICK, D - JOIST_THICK], p.wood, p.woodDark, { width: 1.4 });
  });

  rim(0, arrive(work.rims, 1, 2, 1.4));
  xs.forEach((x, i) => {
    const k = arrive(work.marks, i, xs.length, 2);
    if (k <= 0 || work.joists >= 1) {
      return;
    }
    layoutMark(ink, proj, x, y1, JOIST_THICK / 2, k);
  });

  // The deck: sheets laid back course first, glued and screwed down.
  SHEETS.forEach((sheet, i) => {
    const k = arrive(work.deck, i, SHEETS.length, 1.4);
    if (k <= 0) {
      return;
    }
    const y = y1 + (1 - ease.out(k)) * 1.5;
    const topY = y + SHEET;
    ink.shape(
      [at(proj, sheet.x0, topY, sheet.z0), at(proj, sheet.x1, topY, sheet.z0), at(proj, sheet.x1, topY, sheet.z1), at(proj, sheet.x0, topY, sheet.z1)],
      { fill: p.wood, fillAlpha: 0.55 + 0.4 * k, width: 2, offset: 1, overshoot: false },
    );
    if (sheet.z0 === 0) {
      ink.shape([at(proj, sheet.x0, y, 0), at(proj, sheet.x1, y, 0), at(proj, sheet.x1, topY, 0), at(proj, sheet.x0, topY, 0)], {
        fill: p.woodDark,
        width: 1.4,
        offset: 0,
        overshoot: false,
      });
    }
    // The screw lines, along the joists under it, once it is down.
    if (k >= 1) {
      for (const x of xs) {
        if (x <= sheet.x0 + 0.05 || x >= sheet.x1 - 0.2) {
          continue;
        }
        for (let z = sheet.z0 + 0.4; z < sheet.z1 - 0.2; z += 0.9) {
          const c = at(proj, x + JOIST_THICK / 2, topY, z);
          ink.dot(c.x, c.y, 1.4, p.ink, 0.6);
        }
      }
    }
  });
  if (work.joints > 0) {
    for (const sheet of SHEETS) {
      if (sheet.x0 === 0) {
        continue;
      }
      const a = at(proj, sheet.x0, y1 + SHEET, sheet.z0);
      const b = at(proj, sheet.x0, y1 + SHEET, sheet.z1);
      ink.line(a.x, a.y, mix(a.x, b.x, ease.out(work.joints)), mix(a.y, b.y, ease.out(work.joints)), { width: 5, color: p.accent, overshoot: false });
    }
  }
}

/** A layout mark on a rim's top: a line square across it, and an X on the joist's side of it. */
function layoutMark(ink: Ink, proj: Projection, x: number, y: number, z: number, k: number): void {
  const a = at(proj, x, y, z - 0.1);
  const b = at(proj, x, y, z + 0.1);
  ink.line(a.x, a.y + 5, mix(a.x, b.x, k), mix(a.y + 5, b.y - 5, k), { width: 2, overshoot: false });
  if (k > 0.5) {
    const c = at(proj, x + JOIST_THICK / 2, y, z);
    const s = 5 * ease.back(span(k, 0.5, 1));
    ink.line(c.x - s, c.y - s, c.x + s, c.y + s, { width: 2, color: ink.palette.signal, overshoot: false });
    ink.line(c.x + s, c.y - s, c.x - s, c.y + s, { width: 2, color: ink.palette.signal, overshoot: false });
  }
}

/** The frame's outside corners on the rims' tops: front left, front right, back left, back right. */
export function frameCorners(proj: Projection, base: number): [Pt, Pt, Pt, Pt] {
  const y = base + JOIST;
  return [at(proj, 0, y, 0), at(proj, W, y, 0), at(proj, 0, y, D), at(proj, W, y, D)];
}
